import type { Tone } from '../design/components.js';

/**
 * Libellés et états communs aux écrans de la plateforme (santé, modules, accueil
 * d'administration) : même couleur et même raison pour un même état partout.
 */

export type PlatformStatus = 'ok' | 'degraded' | 'down';

const STATUS: Record<PlatformStatus, { label: string; tone: Tone; rank: number }> = {
  ok: { label: 'Opérationnel', tone: 'ok', rank: 0 },
  degraded: { label: 'Dégradé', tone: 'warning', rank: 1 },
  down: { label: 'En panne', tone: 'critical', rank: 2 },
};

/** Libellé et ton d'un état de service ou de sauvegarde. */
export function statusInfo(status: PlatformStatus): { label: string; tone: Tone } {
  const s = STATUS[status];
  return { label: s.label, tone: s.tone };
}

/** Rang de gravité (tri : les plus graves d'abord en ordre décroissant). */
export function statusRank(status: PlatformStatus): number {
  return STATUS[status].rank;
}

/** Liseré de ligne d'un état (aucun pour un état normal). */
export function statusRowTone(status: PlatformStatus): 'warning' | 'critical' | undefined {
  return status === 'down' ? 'critical' : status === 'degraded' ? 'warning' : undefined;
}

export const SERVICE_LABELS: Record<string, string> = {
  api: 'API Oasis',
  worker: 'Traitements de fond',
  web: 'Interface web',
  postgres: 'Base de données',
  keycloak: 'Authentification (Keycloak)',
  nextcloud: 'Fichiers (Nextcloud)',
  'dossiers-cloud': 'Dossiers cloud (Nextcloud)',
  proxy: 'Proxy (Traefik)',
};

export const BACKUP_LABELS: Record<string, string> = {
  'database-sftp': 'Base de données (SFTP)',
  'files-sftp': 'Fichiers (SFTP)',
  'config-ovh': 'Configuration (OVH)',
};

export const MODULE_STATUS: Record<string, { label: string; tone: Tone }> = {
  enabled: { label: 'Actif', tone: 'ok' },
  disabled: { label: 'Désactivé', tone: 'neutral' },
  installed: { label: 'Installé', tone: 'info' },
};

/** Date et heure courtes (« 30/10/2026 08:15 »), ou le texte de remplacement. */
export function formatDate(value: string | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined) return fallback;
  return new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Nombre de jours entiers avant une date (négatif si dépassée). */
export function daysUntil(value: string, now: number): number {
  return Math.floor((new Date(value).getTime() - now) / 86_400_000);
}
